(function () {
  // Mobile is where the Topics menu / Articles drill-in live.
  const isMobile = () => window.matchMedia('(max-width: 1023px)').matches;

  // --- Generic toggle dropdown (Physician Resources + Articles) -------------
  function initDropdown(dropdown) {
    const toggle = dropdown.querySelector('[aria-haspopup]');
    const menu = dropdown.querySelector('[id]');
    if (!toggle || !menu) return;

    const links = Array.from(menu.querySelectorAll('a'));

    // Mobile drill-in wiring (only present on the main-nav "Articles" dropdown).
    const backBtn = dropdown.querySelector('.main-nav__dropdown-back');
    const linksList = dropdown.closest('.main-nav__links');

    // The "primary" layer to hide from assistive tech while drilled in: the
    // sibling rows plus this dropdown's own toggle (its menu stays active).
    function primaryEls() {
      if (!linksList) return [];
      const els = Array.from(linksList.children).filter((li) => li !== dropdown);
      els.push(toggle);
      return els;
    }

    function open() {
      dropdown.classList.add('js-open');
      toggle.setAttribute('aria-expanded', 'true');
      if (isMobile() && linksList) {
        // Grow the in-flow white panel to fit the absolute sub-panel.
        linksList.style.minHeight = menu.scrollHeight + 'px';
        // Isolate layers for AT: reveal the sub-panel, hide the primary rows.
        menu.inert = false;
        primaryEls().forEach((el) => { el.inert = true; });
        (backBtn || links[0] || toggle).focus();
      }
    }

    function close(returnFocus) {
      dropdown.classList.remove('js-open');
      toggle.setAttribute('aria-expanded', 'false');
      if (isMobile() && linksList) {
        linksList.style.minHeight = '';
        primaryEls().forEach((el) => { el.inert = false; });
        menu.inert = true; // sub-panel back off-screen and hidden from AT
      }
      if (returnFocus) toggle.focus();
    }

    function isOpen() {
      return dropdown.classList.contains('js-open');
    }

    if (backBtn) {
      backBtn.addEventListener('click', function () {
        close(true);
      });
    }

    // Toggle on button click
    toggle.addEventListener('click', function () {
      isOpen() ? close() : open();
    });

    // Close on outside click
    document.addEventListener('click', function (e) {
      if (isOpen() && !dropdown.contains(e.target)) {
        close();
      }
    });

    // Close on ESC and return focus to toggle. stopImmediatePropagation keeps
    // this a distinct step from the Topics-menu ESC handler, so on mobile the
    // first ESC only backs out of the sub-panel.
    document.addEventListener('keyup', function (e) {
      if (isOpen() && (e.key === 'Escape' || e.key === 'Esc')) {
        e.stopImmediatePropagation();
        close(true);
      }
    });

    // Arrow key navigation within the dropdown
    menu.addEventListener('keydown', function (e) {
      const index = links.indexOf(document.activeElement);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        (links[index + 1] || links[0]).focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        (links[index - 1] || links[links.length - 1]).focus();
      }
    });

    // Close when focus leaves the last link (Tab forward)
    const lastLink = links[links.length - 1];
    if (lastLink) {
      lastLink.addEventListener('focusout', function (e) {
        if (!dropdown.contains(e.relatedTarget)) {
          close();
        }
      });
    }
  }

  const dropdowns = document.querySelectorAll('.util-nav__dropdown, .main-nav__dropdown');
  dropdowns.forEach(initDropdown);

  // The component root (<header class="global-header phy-blog-main-nav">) is the
  // sticky element, the util-nav-height host, and the focus-trap root.
  const nav = document.querySelector('.phy-blog-main-nav');

  // --- Sticky main-nav ------------------------------------------------------
  const utilNav = nav && nav.querySelector('.util-nav');
  if (nav && utilNav) {
    function setUtilNavHeight() {
      nav.style.setProperty(
        '--phy-blog-util-nav-height',
        utilNav.getBoundingClientRect().height + 'px'
      );
      document.documentElement.style.setProperty(
        '--phy-blog-main-nav-height',
        (nav.getBoundingClientRect().height - utilNav.getBoundingClientRect().height) + 'px'
      );
    }

    setUtilNavHeight();
    window.addEventListener('resize', setUtilNavHeight);
  }

  // --- Search suggestions (open on focus) -----------------------------------
  const search = document.querySelector('.main-nav__search');
  if (search) {
    const input = search.querySelector('.main-nav__search-input');

    function openSearch() {
      search.classList.add('js-search-open');
      input.setAttribute('aria-expanded', 'true');
    }

    function closeSearch() {
      search.classList.remove('js-search-open');
      input.setAttribute('aria-expanded', 'false');
    }

    input.addEventListener('focus', openSearch);

    // Mobile "← Back" control closes the suggestions. Don't return focus to the
    // input (its focus handler would immediately re-open the panel); blur the
    // active element instead so the panel stays closed.
    const searchBack = search.querySelector('.main-nav__search-back');
    if (searchBack) {
      searchBack.addEventListener('click', function () {
        closeSearch();
        if (document.activeElement) document.activeElement.blur();
      });
    }

    document.addEventListener('click', function (e) {
      if (!search.contains(e.target)) {
        closeSearch();
      }
    });

    // Close when keyboard focus leaves the search (e.g. Tab past suggestions)
    search.addEventListener('focusout', function (e) {
      if (!search.contains(e.relatedTarget)) {
        closeSearch();
      }
    });

    search.addEventListener('keyup', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        closeSearch();
        input.focus();
      }
    });
  }

  // --- Mobile "Topics" toggle -----------------------------------------------
  const mainNav = nav && nav.querySelector('.header-bar__container');
  const topicsToggle = mainNav && mainNav.querySelector('.main-nav__topics-toggle');
  if (mainNav && topicsToggle) {
    const articlesMenu = mainNav.querySelector('.main-nav__dropdown-menu');

    function isTopicsOpen() {
      return mainNav.classList.contains('js-topics-open');
    }

    // Take the rest of the page out of the tab order / AT tree while the menu
    // is open, so focus stays within the nav (WCAG 2.1.2 / 2.4.3). The nav's
    // own header (util-nav) stays interactive.
    function setBackgroundInert(on) {
      if (!nav || !nav.parentElement) return;
      Array.from(nav.parentElement.children).forEach((el) => {
        if (el !== nav) el.inert = on;
      });
    }

    function openTopics() {
      mainNav.classList.add('js-topics-open');
      topicsToggle.setAttribute('aria-expanded', 'true');
      setBackgroundInert(true);
      // Prime the Articles sub-panel as inert (not drilled in yet) so its
      // off-screen content isn't exposed to assistive tech.
      if (articlesMenu && isMobile()) articlesMenu.inert = true;
    }

    function closeTopics() {
      mainNav.classList.remove('js-topics-open');
      topicsToggle.setAttribute('aria-expanded', 'false');
      // Reset any drilled-in Articles sub-panel.
      const openDrop = mainNav.querySelector('.main-nav__dropdown.js-open');
      if (openDrop) {
        openDrop.classList.remove('js-open');
        const t = openDrop.querySelector('[aria-haspopup]');
        if (t) t.setAttribute('aria-expanded', 'false');
      }
      // Clear inert flags / inline styles left by the drill-in.
      mainNav
        .querySelectorAll(
          '.main-nav__links > li, .main-nav__dropdown-toggle, .main-nav__dropdown-menu'
        )
        .forEach((el) => { el.inert = false; });
      const list = mainNav.querySelector('.main-nav__links');
      if (list) list.style.minHeight = '';
      setBackgroundInert(false);
    }

    topicsToggle.addEventListener('click', function () {
      if (isTopicsOpen()) {
        closeTopics();
      } else {
        openTopics();
      }
    });

    document.addEventListener('click', function (e) {
      if (isTopicsOpen() && !mainNav.contains(e.target)) {
        closeTopics();
      }
    });

    document.addEventListener('keyup', function (e) {
      // Only close the whole menu when no sub-panel is open; the sub-panel's
      // own ESC handler (which stops propagation) handles the first press.
      if (
        isTopicsOpen() &&
        !mainNav.querySelector('.main-nav__dropdown.js-open') &&
        (e.key === 'Escape' || e.key === 'Esc')
      ) {
        closeTopics();
        topicsToggle.focus();
      }
    });

    // Contain Tab focus within the nav while the mobile menu is open. Inert
    // regions (background page, hidden drill layer) are already excluded.
    nav.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !isTopicsOpen() || !isMobile()) return;
      const focusable = Array.from(
        nav.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.closest('[inert]') && el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // If the viewport grows past the mobile breakpoint while the menu is open,
    // tear it down so leftover `inert`/inline styles don't disable the desktop
    // layout.
    const mq = window.matchMedia('(max-width: 1023px)');
    const onBreakpointChange = function (e) {
      if (!e.matches && isTopicsOpen()) closeTopics();
    };
    if (mq.addEventListener) {
      mq.addEventListener('change', onBreakpointChange);
    } else if (mq.addListener) {
      mq.addListener(onBreakpointChange); // Safari < 14 fallback
    }
  }
}());
